import type { AmplitudeClient } from 'amplitude-js';
import { ZodiacTypes } from '@packages/shared/core/zodiac/entities';
import { Config } from '@packages/shared/interfaces';
import { CrossDomainStorage } from '@packages/shared/core/common/services';
import { UtmTags } from '@packages/shared/core/user/entities';
import { UtmTagsService } from '@packages/shared/core/user/services';
import { ObservabilitySystem } from '@packages/shared/core/common/observability/interfaces';
import { SpanNames } from '@packages/shared/core/common/observability/entities';
import { isSuccessfulStatusCode } from '@packages/shared/core/common/utils/http/isSuccessfulStatusCode';
import { FeatureFlagsAttributes } from '@packages/shared/core/feature-flags/entities';
import { QUIZ_ANALYTIC_EVENTS } from '../../events';
import { AmplitudeService } from './AmplitudeService';

export class AmplitudeClientService implements AmplitudeService {
  private instance?: AmplitudeClient;

  constructor(
    private utmTagsService: UtmTagsService,
    private readonly config: Config,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly crossDomainStorage: CrossDomainStorage,
  ) {
    this.utmTagsService = utmTagsService;
    this.config = config;
    this.observabilitySystem = observabilitySystem;

    this.updateUtmTags(this.utmTagsService.getUtmTags());
  }

  async updateUtmTags(utmTags: UtmTags) {
    await this.setUserProperties({
      media_source: utmTags.utm_source,
      ...utmTags,
    });
  }

  async getDeviceId(): Promise<string> {
    const instance = await this.getInstance();
    return instance.options.deviceId || '';
  }

  async getSessionId(): Promise<string> {
    const instance = await this.getInstance();
    return `${instance.getSessionId()}`;
  }

  async setUserProperties(userData: Record<string, unknown>) {
    const instance = await this.getInstance();
    instance.setUserProperties(userData);
  }

  async setUserId(userId: string) {
    const instance = await this.getInstance();
    instance.setUserId(userId);
  }

  async launchFirstTime() {
    return this.logEvent('launch_first_time', { funnel_stage: 'quiz' });
  }

  async launch() {
    return this.logEvent('launch', { funnel_stage: 'quiz' });
  }

  async trackQuizEvent(quizEvent: QUIZ_ANALYTIC_EVENTS, eventProperties: Record<string, unknown> = {}) {
    return this.logEvent(quizEvent, eventProperties);
  }

  async privateDataCollectionAgreed() {
    return this.logEvent('flow_intro_accept_cookies');
  }

  async setCurrentQuizName(quizName: string) {
    const instance = await this.getInstance();
    const identify = new instance.Identify();
    identify.setOnce('flow', quizName);
    return instance.identify(identify);
  }

  async extendedLpOpened() {
    this.setUserProperties({
      landing_page_id: 'lp_extended-landing',
    });
    return this.logEvent('lp_extended_open');
  }

  async extendedLpGetReport() {
    return this.logEvent('lp_extended_get_report_click');
  }

  async registrationLpOpened(value: string) {
    return this.logEvent('registration_screen_open', {
      context: value,
    });
  }

  async registrationLpClicked(value: string) {
    return this.logEvent('registration_screen_clicked', {
      context: value,
    });
  }

  async registrationFormClicked(value: string) {
    return this.logEvent('registration_emailform_clicked', {
      context: value,
    });
  }

  registrationCatalogueLpOpen() {
    return this.logEvent('registration_catalogue_open');
  }

  registrationExpertLpOpen(expertId: string) {
    return this.logEvent('registration_expert_open', {
      expert_id: expertId,
    });
  }

  registrationStartChatClicked(expertId: string) {
    return this.logEvent('registration_start_chat', {
      expert_id: expertId,
    });
  }

  setUserExperimentGroupName(experimentName: string, groupName: string) {
    this.setUserProperties({
      [`quiz_${experimentName}`]: groupName,
    });
  }

  expertsCatalogueLpOpened() {
    return this.logEvent('catalogue_screen_open');
  }

  expertsCatalogueGetStartedClicked() {
    return this.logEvent('catalogue_getstarted_click');
  }

  expertsCatalogueLpStartChatClicked(id: string) {
    return this.logEvent('catalogue_start_chat', {
      expert_id: id,
    });
  }

  expertsCatalogueEmailFormOpened() {
    return this.logEvent('catalogue_emailform_open', {
      event_value: 'common',
    });
  }

  expertsCatalogueGoogleFormOpened() {
    return this.logEvent('catalogue_emailform_open', {
      event_value: 'google',
    });
  }

  expertsCatalogueLpEmailConfirmOpened() {
    return this.logEvent('catalogue_email_consent_open');
  }

  expertsCatalogueLpPrelandingOpened() {
    return this.logEvent('catalogue_prelanding_open');
  }

  expertsCatalogueLpExpertOpened(id: string) {
    return this.logEvent('catalogue_expert_open', {
      expert_id: id,
    });
  }

  expertsCatalogueMatchedExpertsOpened() {
    return this.logEvent('catalogue_matched_experts');
  }

  expertsCatalogueAllExpertsOpened() {
    return this.logEvent('catalogue_all_experts');
  }

  expertsCatalogueEmailConsentClick(value: boolean) {
    return this.logEvent('catalogue_email_consent_clicked', {
      context: value ? 'opted_in' : 'refused',
    });
  }

  registrationEmailConsentClick(value: boolean) {
    return this.logEvent('registration_email_consent_clicked', {
      context: value ? 'opted_in' : 'refused',
    });
  }

  async setEmailConsent(emailConsent: boolean) {
    await this.setUserProperties({
      email_consent: emailConsent,
    });
  }

  smsInputScreenClicked(data: { eventValue: 'opted_in' | 'closed' }) {
    return this.logEvent('sms_input_screen_clicked', {
      event_value: data.eventValue,
      context: 'after_password',
      funnel_stage: 'quiz',
    });
  }

  registrationOfferEmailConfirmClicked(context: string) {
    return this.logEvent('registration_offer_email_confirm_clicked', {
      context,
    });
  }

  registrationOfferEmailConfirmSkipped(context: string) {
    return this.logEvent('registration_offer_email_confirm_skipped', {
      context,
    });
  }

  registrationOfferEmailChangeEmail(context: string) {
    return this.logEvent('registration_offer_email_change_email', {
      context,
    });
  }

  appPageAppButtonClick() {
    return this.logEvent('apppage_app_button_click');
  }

  appPageOpened() {
    return this.logEvent('apppage_open');
  }

  zodiacPairCompatibilityPageOpen(zodiac: ZodiacTypes, zodiacToCompare: ZodiacTypes) {
    return this.logEvent('seo_zodiac_pair_compatibility_page_open', {
      zodiac,
      zodiac_to_compare: zodiacToCompare,
    });
  }

  zodiacPairCompatibilityPageZodiacSignClick(data: {
    zodiac: ZodiacTypes;
    zodiacToCompare: ZodiacTypes;
    clickedZodiac: ZodiacTypes;
  }) {
    return this.logEvent('seo_zodiac_pair_compatibility_page_other_zodiac_sign_click', {
      zodiac: data.zodiac,
      zodiac_to_compare: data.zodiacToCompare,
      zodiac_clicked: data.clickedZodiac,
    });
  }
  geoMainPageOpen() {
    return this.logEvent('seo_geo_main_page_open');
  }

  geoMainPageExpertsCardClick() {
    return this.logEvent('seo_geo_main_page_expert_card_click');
  }

  geoMainPageStartChatClick() {
    return this.logEvent('seo_geo_main_page_start_chat_click');
  }

  geoMainPageBrowseAdvisorsClick() {
    return this.logEvent('seo_geo_main_page_browse_advisors_click');
  }

  geoMainPageFindAdvisorClick() {
    return this.logEvent('seo_geo_main_page_find_advisor_click');
  }

  signUpPageOpened() {
    return this.logEvent('seo_signup_page_opened');
  }

  signUpPageLoginClick() {
    return this.logEvent('seo_sign_up_page_login_click');
  }

  signUpPageSignUpClick() {
    return this.logEvent('seo_sign_up_page_signup_click');
  }

  registrationWelcomeBackClick() {
    return this.logEvent('registration_welcome_back_click');
  }

  registrationWelcomeBackEmailError() {
    return this.logEvent('registration_welcome_back_email_error');
  }

  registrationWelcomeBackPasswordError() {
    return this.logEvent('registration_welcome_back_password_error');
  }

  registrationWelcomeBackLoginSuccess() {
    return this.logEvent('registration_welcome_back_login_success');
  }

  registrationWelcomeBackForgotYourPasswordClick() {
    return this.logEvent('registration_welcome_back_forgot_your_password_click');
  }

  registrationPasswordRecoveryOpen() {
    return this.logEvent('registration_password_recovery_open');
  }

  registrationPasswordRecoveryClick() {
    return this.logEvent('registration_password_recovery_click');
  }

  registrationCheckEmailOpen() {
    return this.logEvent('registration_check_email_open');
  }

  registrationCheckEmailClick() {
    return this.logEvent('registration_check_email_click');
  }

  tarotGeoMainPageOpen() {
    return this.logEvent('seo_tarot_geo_main_page_open');
  }

  tarotGeoMainPageExpertsCardClick() {
    return this.logEvent('seo_tarot_geo_main_page_expert_card_click');
  }

  tarotGeoMainPageStartChatClick() {
    return this.logEvent('seo_tarot_geo_main_page_start_chat_click');
  }

  tarotGeoMainPageBrowseAdvisorsClick() {
    return this.logEvent('seo_tarot_geo_main_page_browse_advisors_click');
  }

  tarotGeoMainPageFindAdvisorClick() {
    return this.logEvent('seo_tarot_geo_main_page_find_advisor_click');
  }

  expertLandingPageScreenOpened(category: string) {
    return this.logEvent('expert_landing_page_screen_open', {
      context: category,
    });
  }

  expertLandingPageScreenClicked(category: string) {
    return this.logEvent('expert_landing_page_screen_click', {
      context: category,
    });
  }

  expertCatalogueLandingPageScreenOpened() {
    return this.logEvent('expert_catalogue_landing_screen_open');
  }

  startFreeChatScreenClicked() {
    return this.logEvent('start_free_chat_screen_click');
  }

  expertPagePaginationButtonClicked(page: number) {
    return this.logEvent('next_expert_page_button_click', {
      context: page,
    });
  }

  seoBannerClick() {
    return this.logEvent('seo_banner_click');
  }

  trackUserAge(age: number) {
    this.setUserProperties({
      user_age: age,
    });
  }

  notFoundPageOpen() {
    return this.logEvent('seo_not_found_page_open');
  }

  serverErrorPageOpen() {
    return this.logEvent('seo_server_error_page_open');
  }

  setFeatureFlagsAttributes(attributes: FeatureFlagsAttributes): void {
    this.setUserProperties({
      feature_flags_attributes: attributes,
    });
  }

  // TODO [MARTECH-913]: Migrate to @amplitude/analytics-browser v2. The legacy amplitude-js v8 SDK is EOL.
  // Its logEvent callbacks are not guaranteed to fire (offline/buffered sends), which can leak spans.
  // The new SDK's track() returns a Promise, enabling reliable span lifecycle via startActiveSpan + async/await.
  async logEvent(eventName: string, data?: unknown) {
    const span = this.observabilitySystem.startSpan({ name: SpanNames.TRACK_ANALYTICS_AMPLITUDE });
    const instance = await this.getInstance();

    instance.logEvent(
      eventName,
      data,
      (code) => {
        if (isSuccessfulStatusCode(code)) {
          span.succeed();
        }
      },
      (_code, _body, details) => {
        span.fail(details?.reason || 'logEvent failed');
      },
    );
  }

  private async getInstance(): Promise<AmplitudeClient> {
    if (this.instance) return this.instance;
    const { default: amplitude } = await import('amplitude-js');
    this.instance = amplitude.getInstance();
    const userId = this.crossDomainStorage.getNebulaId();
    this.instance.init(this.config.amplitudeApiKey, userId, {
      domain: this.config.domainRoot,
      apiEndpoint: this.config.amplitudeProxyUrl,
      deviceIdFromUrlParam: true,
    });
    return this.instance;
  }
}
